import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/ui-components/ui/sheet";
import { Search, X, Check, ChevronRight, Loader2, MousePointer } from "lucide-react";
import { EntitySlotSelectionMap, EntityInstance, SelectorSelectionMap, AttributeOverrideMap } from "../types/PostGenerationTypes";
import { getMediaTypeIcon } from '../../template-editor/components/content-selector/utils';
import { SelectionStaticInputRenderer } from './SelectionInputComponents';
import { useEntityRegistry } from "../../template-editor/hooks/useEntityRegistry";
import { SearchResult, EntityRegistry } from "../../template-editor/registry/entities/entityRegistry";
import { useOnboardingTourStep } from '@/util-components/onboarding/tour-step-store';
import { cn } from '@/lib/utils/cn';

interface SourceSelectionSectionProps {
  /** Slot-based entity selection */
  entitySlots: EntitySlotSelectionMap;
  /** Callback for slot changes */
  onSlotsChange: (slots: EntitySlotSelectionMap) => void;
  /** Selector-based value selection */
  selectors?: SelectorSelectionMap;
  /** Callback for selector changes */
  onSelectorsChange?: (selectors: SelectorSelectionMap) => void;
  /** Callback for proceed to preview */
  onProceedToPreview?: () => void;
  /** Manual overrides for missing attributes */
  manualOverrides?: AttributeOverrideMap;
  /** Callback for updating manual overrides */
  onManualOverride?: (slotKey: string, attributePath: string, value: any) => void;
}

/**
 * Mobile-optimized selection item using Bottom Sheet
 * Robust implementation preventing keyboard/z-index issues
 */
// Helper to get human-readable attribute labels
const getAttributeLabel = (registry: EntityRegistry, attributePath: string, entityType: string): string => {
  // Try to find definition in registry
  if (registry) {
    const def = registry.get(entityType);
    if (def && def.attributes) {
      const attr = def.attributes.find((a: any) => a.attributeKey === attributePath);
      if (attr && attr.attributeLabel) return attr.attributeLabel;
    }
  }

  // Map common attribute paths to readable labels
  const labelMap: Record<string, string> = {
    'content.staticValue': 'Textinhalt',
    'source.staticValue': 'Bildquelle',
    'fill.color.staticValue': 'Hintergrundfarbe',
    'stroke.color.staticValue': 'Rahmenfarbe',
    // Add more mappings as needed
  };

  if (labelMap[attributePath]) {
    return labelMap[attributePath];
  }

  // Fallback: format the path (e.g., "content.staticValue" -> "Inhalt")
  const parts = attributePath.split('.');
  if (parts.length > 0) {
    const lastPart = parts[0];
    if (lastPart === 'content') return 'Inhalt';
    if (lastPart === 'source') return 'Bild/Grafik';
    if (lastPart === 'fill') return 'Hintergrund';
    if (lastPart === 'stroke') return 'Rahmen';
    return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
  }
  
  return attributePath;
};

const getEntityPreviewUrl = (source?: any): string | undefined => {
  if (!source) return undefined;

  return (
    source.selectedImage?.thumbnailUrl ||
    source.selectedImage?.url ||
    source.thumbnailUrl ||
    source.imageUrl ||
    source.imageState?.active?.thumbnailUrl ||
    source.imageState?.active?.url ||
    source.logoState?.active?.thumbnailUrl ||
    source.logoState?.active?.url
  );
};

interface EntityImageOption {
  _id: string;
  url?: string;
  thumbnailUrl?: string;
}

const getEntityImageOptions = (source?: any): EntityImageOption[] => {
  if (!source?.imageStates || !Array.isArray(source.imageStates)) return [];

  return source.imageStates
    .map((entry: any) => ({
      _id: entry._id,
      url: entry.state?.active?.url,
      thumbnailUrl: entry.state?.active?.thumbnailUrl,
    }))
    .filter((entry: EntityImageOption) => !!entry._id && (!!entry.url || !!entry.thumbnailUrl));
};

const getSelectedEntityImageId = (source?: any): string | undefined => {
  const options = getEntityImageOptions(source);
  if (source?.selectedImageAssetId) return source.selectedImageAssetId;
  if (!source?.imageUrl) return options[0]?._id;

  return options.find((option) => option.url === source.imageUrl || option.thumbnailUrl === source.thumbnailUrl)?._id ?? options[0]?._id;
};

const applyEntityImageOption = (source: any, option: EntityImageOption) => ({
  ...source,
  imageUrl: option.url || source.imageUrl,
  thumbnailUrl: option.thumbnailUrl || option.url || source.thumbnailUrl,
  selectedImageAssetId: option._id,
  selectedImage: {
    url: option.url,
    thumbnailUrl: option.thumbnailUrl,
  },
});

const EntityImagePicker = ({
  source,
  onSelect,
}: {
  source?: any;
  onSelect: (option: EntityImageOption) => void;
}) => {
  const options = getEntityImageOptions(source);
  const selectedId = getSelectedEntityImageId(source);

  if (options.length <= 1) return null;

  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 py-2">
      {options.map((option, index) => {
        const previewUrl = option.thumbnailUrl || option.url;
        const isSelected = selectedId === option._id;

        return (
          <button
            key={option._id}
            type="button"
            onClick={() => onSelect(option)}
            className={cn(
              "relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white transition-all",
              isSelected ? "border-blue-500 ring-4 ring-blue-500/10" : "border-gray-200 hover:border-gray-300"
            )}
            title={`Bild ${index + 1}`}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="" className="h-full w-full object-cover object-top" />
            ) : (
              <MousePointer className="size-4 text-gray-300" />
            )}
            {isSelected && (
              <span className="absolute bottom-1 right-1 flex size-4 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm">
                <Check className="size-2.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const EntityPreview = ({
  entityType,
  source,
  registry,
  iconClassName,
  className,
  imageClassName,
}: {
  entityType: string;
  source?: any;
  registry: EntityRegistry;
  iconClassName: string;
  className: string;
  imageClassName?: string;
}) => {
  const previewUrl = getEntityPreviewUrl(source);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = !!previewUrl && failedUrl !== previewUrl;

  return (
    <div className={className}>
      {showImage ? (
        <img
          src={previewUrl}
          alt=""
          className={cn("h-full w-full object-contain", imageClassName)}
          onError={() => setFailedUrl(previewUrl)}
        />
      ) : (
        registry.getEntityIcon(entityType, iconClassName)
      )}
    </div>
  );
};

const MobileSourceSelectionItem: React.FC<{
  slot: EntitySlotSelectionMap[string];
  searchTerm: string;
  searchResults: SearchResult[];
  isSearching: boolean;
  isLoadingEntity: boolean;
  onSearchTermChange: (term: string) => void;
  onSelectResult: (result: SearchResult) => void;
  onDeselect: () => void;
  onSelectEntityImage: (option: EntityImageOption) => void;
  onOpen?: () => void;
  registry: EntityRegistry;
  manualOverrides?: AttributeOverrideMap;
  onManualOverride?: (slotKey: string, attributePath: string, value: any) => void;
}> = ({
  slot,
  searchTerm,
  searchResults,
  isSearching,
  isLoadingEntity,
  onSearchTermChange,
  onSelectResult,
  onDeselect,
  onSelectEntityImage,
  onOpen,
  registry,
  manualOverrides,
  onManualOverride
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Close sheet when item is selected
  useEffect(() => {
    if (slot.selectedInstance) {
      setIsOpen(false);
    }
  }, [slot.selectedInstance]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && onOpen) {
      onOpen();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <div className={`flex flex-col p-3 bg-white border rounded-xl mb-3 shadow-sm transition-colors ${
        slot.missingAttributes?.length ? 'border-orange-200' : 'border-gray-100'
      }`}>
         <div className="flex items-center gap-3">
             {/* Icon */}
             <div className="relative h-10 w-10 flex-shrink-0">
               <div className={`flex h-full w-full items-center justify-center overflow-hidden rounded-lg ${
                  slot.missingAttributes?.length ? 'bg-orange-50 text-orange-600' : 'bg-gray-100 text-gray-600'
               }`}>
                 <EntityPreview
                    entityType={slot.entityType}
                    source={slot.selectedInstance?.data}
                    registry={registry}
                    iconClassName="w-5 h-5"
                    className="flex h-full w-full items-center justify-center"
                 />
               </div>
               {slot.required && !slot.selectedInstance && (
                  <div className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500"></div>
               )}
             </div>
    
             {/* Content Area (Trigger) */}
             <SheetTrigger asChild>
                 <div className="flex-1 min-w-0 text-left cursor-pointer">
                     {slot.selectedInstance ? (
                         <div>
                             <div className="text-xs text-gray-400 mb-0.5">{slot.displayLabel}</div>
                             <div className="font-medium truncate flex items-center gap-1.5 text-gray-900">
                                 {slot.selectedInstance.label}
                             </div>
                         </div>
                     ) : (
                         <div>
                             <div className="text-sm font-medium text-gray-900">{slot.displayLabel}</div>
                             <div className="text-xs text-gray-500">Bitte auswählen...</div>
                         </div>
                     )}
                 </div>
             </SheetTrigger>
    
             {/* Action Button */}
             {slot.selectedInstance ? (
                 <Button
                     variant="ghost"
                     size="icon"
                     className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                     onClick={(e) => {
                         e.stopPropagation();
                         onDeselect();
                     }}
                 >
                     <X className="h-4 w-4" />
                 </Button>
             ) : (
                <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </SheetTrigger>
             )}
         </div>

         {slot.selectedInstance && (
            <div className="mt-3 pl-[52px]">
              <EntityImagePicker source={slot.selectedInstance.data} onSelect={onSelectEntityImage} />
            </div>
         )}

         {/* Missing Attributes Inputs (Mobile) - Minimalist */}
         {slot.selectedInstance && slot.missingAttributes && slot.missingAttributes.length > 0 && (
            <div className="mt-4 space-y-4 pt-4 border-t border-gray-100">
                {/* Mobile Validation Hint */}
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-orange-600 bg-orange-50/50 px-2 py-1 rounded-md w-fit">
                    <span className="h-1 w-1 rounded-full bg-orange-500" />
                    <span>Fehlende Infos</span>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {slot.missingAttributes.map((attr) => (
                      <div key={attr.attributePath} className="space-y-1.5">
                          <label className="text-[11px] font-bold text-gray-500 uppercase tracking-tight ml-0.5">
                              {getAttributeLabel(registry, attr.attributePath, slot.entityType)}
                          </label>
                          <SelectionStaticInputRenderer
                              mediaType={attr.mediaType}
                              value={manualOverrides?.[slot.slotKey]?.[attr.attributePath]}
                              onChange={(val) => onManualOverride && onManualOverride(slot.slotKey, attr.attributePath, val)}
                              className=""
                              />
                      </div>
                  ))}
                </div>
            </div>
         )}
      </div>

      <SheetContent side="bottom" className="h-[90vh] flex flex-col p-0 rounded-t-2xl z-[9999]">
          <SheetHeader className="p-4 border-b text-left">
              <SheetTitle>{slot.displayLabel} auswählen</SheetTitle>
          </SheetHeader>
          
          <div className="p-4 border-b bg-gray-50/50">
              <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => onSearchTermChange(e.target.value)}
                      className="pl-9 h-11 text-base bg-white" // text-base prevents iOS zoom
                  />
                  {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                  )}
              </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
              {searchResults.length > 0 ? (
                  <div className="space-y-1">
                      {searchTerm.length === 0 && (
                          <div className="px-4 pb-1 pt-2">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Vorschläge</span>
                          </div>
                      )}
                      {searchResults.map((result) => (
                          <button
                              key={result.id}
                              onClick={() => onSelectResult(result)}
                              disabled={isLoadingEntity}
                              className="w-full text-left px-4 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 flex items-center gap-3 transition-colors disabled:opacity-50"
                          >
                             {isLoadingEntity ? (
                                <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 flex-shrink-0">
                                    <Loader2 className="h-5 w-5 animate-spin"/>
                                </div>
                             ) : (
                                <EntityPreview
                                    entityType={slot.entityType}
                                    source={result}
                                    registry={registry}
                                    iconClassName="w-5 h-5"
                                    className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 flex-shrink-0 overflow-hidden"
                                />
                             )}
                             <div className="flex-1 min-w-0">
                                 <div className="font-medium text-gray-900 truncate">{result.label}</div>
                                 {result.subTitle && <div className={`text-xs truncate ${result.subTitle.startsWith('⚠') ? 'text-amber-500' : 'text-gray-500'}`}>{result.subTitle}</div>}
                             </div>
                          </button>
                      ))}
                  </div>
              ) : searchTerm.length >= 2 && !isSearching ? (
                  <div className="text-center py-10 text-gray-500">
                      <p>Keine Ergebnisse gefunden</p>
                  </div>
              ) : isSearching ? (
                  <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
                  </div>
              ) : (
                  <div className="text-center py-10 text-gray-400 text-sm">
                      <p>Tippen Sie, um zu suchen...</p>
                  </div>
              )}
          </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Professional Entity Selection Component
 * Compact, efficient slot-based entity selection with modern SaaS design
 */
export const SourceSelectionSection: React.FC<SourceSelectionSectionProps> = ({
  entitySlots,
  onSlotsChange,
  selectors = {},
  onSelectorsChange,
  onProceedToPreview,
  manualOverrides = {},
  onManualOverride
}) => {
  const [searchTerms, setSearchTerms] = useState<{ [slotKey: string]: string }>({});
  const [searchResults, setSearchResults] = useState<{ [slotKey: string]: SearchResult[] }>({});
  const [isSearching, setIsSearching] = useState<{ [slotKey: string]: boolean }>({});
  const [isLoadingEntity, setIsLoadingEntity] = useState<{ [slotKey: string]: boolean }>({});
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  
  const inputRefs = useRef<{ [slotKey: string]: HTMLInputElement | null }>({});
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRefs = useRef<{ [slotKey: string]: NodeJS.Timeout | null }>({});
  const onboardingTourStep = useOnboardingTourStep();

  // Use the entity registry hook for dependency injection
  const { registry, getAdapter } = useEntityRegistry();
  
  const slotArray = Object.values(entitySlots);
  
  // Group selectors by selectorId and mediaType
  const selectorArray = React.useMemo(() => {
    const rawSelectors = Object.values(selectors);
    const uniqueMap = new Map<string, typeof rawSelectors[0]>();
    
    rawSelectors.forEach(sel => {
      const uniqueKey = `${sel.selectorId}-${sel.mediaType}`;
      if (!uniqueMap.has(uniqueKey)) {
        uniqueMap.set(uniqueKey, sel);
      }
    });
    
    return Array.from(uniqueMap.values());
  }, [selectors]);

  // Calculate progress metrics
  const requiredSlots = slotArray.filter(slot => slot.required);
  const optionalSlots = slotArray.filter(slot => !slot.required);
  const selectedRequiredSlots = requiredSlots.filter(slot => slot.selectedInstance);
  const selectedOptionalSlots = optionalSlots.filter(slot => slot.selectedInstance);
  const allRequiredSelected = selectedRequiredSlots.length === requiredSlots.length;
  const progressPercentage = requiredSlots.length > 0 ? (selectedRequiredSlots.length / requiredSlots.length) * 100 : 100;
  const isOnboardingSelectionButtonLocked =
    onboardingTourStep === 'create-selection' || onboardingTourStep === 'create-preview';

  const handleSelectorValueChange = useCallback((selectorId: string, value: any) => {
    if (!onSelectorsChange) return;
    const updated = { ...selectors };
    updated[selectorId] = { ...updated[selectorId], selectedValue: value } as any;
    onSelectorsChange(updated);
  }, [selectors, onSelectorsChange]);

  /**
   * Calculate dropdown position with retry mechanism
   */
  const calculateDropdownPosition = useCallback((slotKey: string) => {
    const inputElement = inputRefs.current[slotKey];
    if (!inputElement) {
      // Retry after next frame if element not ready
      requestAnimationFrame(() => calculateDropdownPosition(slotKey));
      return;
    }

    const rect = inputElement.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    
    setDropdownPosition({
      top: rect.bottom + scrollY + 2,
      left: rect.left + scrollX,
      width: rect.width
    });
  }, []);



  /**
   * Search for entities. An empty string triggers smart default results from the adapter.
   */
  const handleSearch = useCallback(async (slotKey: string, searchTerm: string) => {
    const slot = entitySlots[slotKey];
    if (!slot) return;

    const adapter = getAdapter(slot.entityType);

    setIsSearching(prev => ({ ...prev, [slotKey]: true }));
    try {
      if (adapter?.search) {
        const results: SearchResult[] = await adapter.search(searchTerm, 5);
        setSearchResults(prev => ({ ...prev, [slotKey]: results }));
      } else {
        console.warn(`No search adapter found for entity type: ${slot.entityType}`);
        setSearchResults(prev => ({ ...prev, [slotKey]: [] }));
      }
    } catch (error) {
      console.error(`Error searching ${slot.entityType}:`, error);
      setSearchResults(prev => ({ ...prev, [slotKey]: [] }));
    } finally {
      setIsSearching(prev => ({ ...prev, [slotKey]: false }));
    }
  }, [entitySlots, getAdapter]);

  /**
   * Load full entity data by ID
   */
  const loadFullEntity = useCallback(async (entityType: string, entityId: string): Promise<EntityInstance | null> => {
    try {
      const adapter = getAdapter(entityType);
      
      if (adapter?.findById) {
        const fullData = await adapter.findById(entityId);
        
        if (fullData) {
          return {
            id: entityId,
            label: registry.generateLabel(entityType, fullData),
            data: fullData,
            type: entityType
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error(`Error loading full entity data for ${entityType}:`, error);
      return null;
    }
  }, [getAdapter, registry]);

  /**
   * Handle entity selection from search results
   * Loads full entity data after selection
   */
  const handleSelectFromSearch = useCallback(async (slotKey: string, searchResult: SearchResult) => {
    const slot = entitySlots[slotKey];
    if (!slot) return;

    setIsLoadingEntity(prev => ({ ...prev, [slotKey]: true }));
    
    try {
      // Load full entity data
      const fullEntity = await loadFullEntity(slot.entityType, searchResult.id);
      
      if (fullEntity) {
        const updatedSlots = { ...entitySlots };
        updatedSlots[slotKey] = {
          ...updatedSlots[slotKey],
          selectedInstance: fullEntity
        };
        onSlotsChange(updatedSlots);
      }
    } catch (error) {
      console.error('Error selecting entity:', error);
    } finally {
      setIsLoadingEntity(prev => ({ ...prev, [slotKey]: false }));
      
      // Clear blur timeout to prevent conflicts
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      
      setActiveSlot(null);
      setDropdownPosition(null);
      setSearchResults(prev => ({ ...prev, [slotKey]: [] }));
      setSearchTerms(prev => ({ ...prev, [slotKey]: '' }));
    }
  }, [entitySlots, onSlotsChange, loadFullEntity]);

  /**
   * Handle entity deselection
   */
  const handleDeselectInstance = useCallback((slotKey: string) => {
    const updatedSlots = { ...entitySlots };
    updatedSlots[slotKey] = {
      ...updatedSlots[slotKey],
      selectedInstance: null
    };
    onSlotsChange(updatedSlots);
  }, [entitySlots, onSlotsChange]);

  const handleSelectEntityImage = useCallback((slotKey: string, option: EntityImageOption) => {
    const slot = entitySlots[slotKey];
    if (!slot?.selectedInstance) return;

    const updatedSlots = { ...entitySlots };
    updatedSlots[slotKey] = {
      ...slot,
      selectedInstance: {
        ...slot.selectedInstance,
        data: applyEntityImageOption(slot.selectedInstance.data, option),
      },
    };
    onSlotsChange(updatedSlots);
  }, [entitySlots, onSlotsChange]);

  /**
   * Handle search input changes with debouncing
   */
  const handleSearchTermChange = useCallback((slotKey: string, term: string) => {
    setSearchTerms(prev => ({ ...prev, [slotKey]: term }));
    
    if (activeSlot === slotKey) {
      calculateDropdownPosition(slotKey);
    }
    
    if (debounceTimeoutRefs.current[slotKey]) {
      clearTimeout(debounceTimeoutRefs.current[slotKey]!);
      debounceTimeoutRefs.current[slotKey] = null;
    }
    
    if (term.length === 0) {
      // Empty → load smart defaults (no textSearch param)
      debounceTimeoutRefs.current[slotKey] = setTimeout(() => {
        handleSearch(slotKey, '');
        debounceTimeoutRefs.current[slotKey] = null;
      }, 300);
    } else {
      // ≥1 char → text search with debounce
      debounceTimeoutRefs.current[slotKey] = setTimeout(() => {
        handleSearch(slotKey, term);
        debounceTimeoutRefs.current[slotKey] = null;
      }, 300);
    }
  }, [handleSearch, activeSlot, calculateDropdownPosition]);

  /**
   * Handle input focus: calculate dropdown position and load defaults if no search term
   */
  const handleInputFocus = useCallback((slotKey: string) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    setActiveSlot(slotKey);
    
    requestAnimationFrame(() => {
      calculateDropdownPosition(slotKey);
    });

    // Trigger default results immediately when field has no input yet
    const currentTerm = searchTerms[slotKey] || '';
    if (currentTerm.length === 0) {
      handleSearch(slotKey, '');
    }
  }, [calculateDropdownPosition, searchTerms, handleSearch]);

  /**
   * Handle input blur with proper timeout management
   */
  const handleInputBlur = useCallback(() => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    // Set new timeout
    blurTimeoutRef.current = setTimeout(() => {
      setActiveSlot(null);
      setDropdownPosition(null);
      blurTimeoutRef.current = null;
    }, 200); // Increased delay for better UX
  }, []);

  /**
   * Close dropdown manually
   */
  const closeDropdown = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setActiveSlot(null);
    setDropdownPosition(null);
  }, []);

  // Close dropdown on scroll or resize with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleCloseDropdown = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(closeDropdown, 100);
    };

    window.addEventListener('scroll', handleCloseDropdown, { passive: true });
    window.addEventListener('resize', handleCloseDropdown);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleCloseDropdown);
      window.removeEventListener('resize', handleCloseDropdown);
    };
  }, [closeDropdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      
      // Clear all debounce timeouts
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(debounceTimeoutRefs.current).forEach(timeout => {
        if (timeout) {
          clearTimeout(timeout);
        }
      });
    };
  }, []);

  if (slotArray.length === 0 && selectorArray.length === 0) {
    return null;
  }

  // Render dropdown content (shared between portal and inline)
  const renderDropdownContent = (slotKey: string) => {
    const slot = entitySlots[slotKey];
    const results = searchResults[slotKey] || [];
    const searchTerm = searchTerms[slotKey] || '';
    const isLoading = isSearching[slotKey];
    const hasResults = results.length > 0;
    const isRecommendMode = searchTerm.length === 0;
    const showNoResults = searchTerm.length >= 2 && !isLoading && !hasResults;

    if (!hasResults && !showNoResults) return null;

    return (
      <div onMouseDown={(e) => e.preventDefault()}>
        {hasResults && (
          <div className="overflow-y-auto max-h-56">
            <div className="p-2">
              {isRecommendMode && (
                <div className="px-3 pb-1 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Vorschläge</span>
                </div>
              )}
              {results.map((searchResult) => (
                <button
                  key={searchResult.id}
                  onClick={() => handleSelectFromSearch(slotKey, searchResult)}
                  disabled={isLoadingEntity[slotKey]}
                  className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 rounded-xl transition-all flex items-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600 transition-all flex-shrink-0 overflow-hidden">
                    {isLoadingEntity[slotKey] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <EntityPreview
                        entityType={slot.entityType}
                        source={searchResult}
                        registry={registry}
                        iconClassName="w-4 h-4"
                        className="flex h-full w-full items-center justify-center"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate text-sm">{searchResult.label}</div>
                    {searchResult.subTitle && (
                      <div className={`text-xs truncate ${searchResult.subTitle.startsWith('⚠') ? 'text-amber-500' : 'text-gray-500'}`}>{searchResult.subTitle}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {showNoResults && (
          <div className="p-4 text-center">
            <div className="text-sm text-gray-500">Keine Ergebnisse gefunden</div>
            <p className="text-xs text-gray-400 mt-1">Versuchen Sie einen anderen Suchbegriff</p>
          </div>
        )}
      </div>
    );
  };

  // Render global dropdown portal (Desktop only)
  const renderDropdown = () => {
    if (!activeSlot || !dropdownPosition) return null;
    
    // Check if we have content to show
    const results = searchResults[activeSlot] || [];
    const searchTerm = searchTerms[activeSlot] || '';
    const isLoading = isSearching[activeSlot];
    const hasResults = results.length > 0;
    const showNoResults = searchTerm.length >= 2 && !isLoading && !hasResults;

    if (!hasResults && !showNoResults) return null;

    return createPortal(
      <div 
        className="hidden lg:block fixed z-[9999] bg-white border-0 rounded-xl shadow-2xl"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: '240px'
        }}
      >
        {renderDropdownContent(activeSlot)}
      </div>,
      document.body
    );
  };

  return (
    <>
      <div className="bg-white lg:overflow-hidden lg:h-full lg:flex lg:flex-col">
        <div data-onboarding="create-post-selection-content" className="flex min-h-0 flex-1 flex-col">
          <div className="px-4 sm:px-6 py-3 sm:py-4 pb-4 sm:pb-6 border-b border-gray-100">
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-100">
                <MousePointer className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">Inhalte auswählen</h3>
                  <div className="text-xs sm:text-sm text-gray-500 font-medium ml-2">
                    {selectedRequiredSlots.length}/{requiredSlots.length}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Wählen Sie alle benötigten Inhalte aus</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            
            {optionalSlots.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                +{selectedOptionalSlots.length} optionale Inhalte ausgewählt
              </div>
            )}
          </div>

          {/* Scrollable Content Area (Desktop) */}
          <div className="lg:flex-1 lg:overflow-y-auto">
            {slotArray.length > 0 && (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {slotArray.map((slot) => (
                  <div key={slot.slotKey}>
                    {/* Mobile View - Robust Bottom Sheet */}
                    <div className="lg:hidden">
                      <MobileSourceSelectionItem
                        slot={slot}
                        searchTerm={searchTerms[slot.slotKey] || ''}
                        searchResults={searchResults[slot.slotKey] || []}
                        isSearching={isSearching[slot.slotKey] || false}
                        isLoadingEntity={isLoadingEntity[slot.slotKey] || false}
                        onSearchTermChange={(term) => handleSearchTermChange(slot.slotKey, term)}
                        onSelectResult={(result) => handleSelectFromSearch(slot.slotKey, result)}
                        onDeselect={() => handleDeselectInstance(slot.slotKey)}
                        onSelectEntityImage={(option) => handleSelectEntityImage(slot.slotKey, option)}
                        onOpen={() => handleSearch(slot.slotKey, searchTerms[slot.slotKey] || '')}
                        registry={registry}
                        manualOverrides={manualOverrides}
                        onManualOverride={onManualOverride}
                      />
                    </div>

                    {/* Desktop View - Existing Implementation */}
                    <div className="hidden lg:block group relative">
                      <div className="flex items-start gap-4">
                        {/* Icon with Required Indicator */}
                        <div className="relative h-11 w-11 flex-shrink-0">
                          <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gray-100 text-gray-600 transition-all group-hover:bg-blue-50 group-hover:text-blue-600">
                            <EntityPreview
                              entityType={slot.entityType}
                              source={slot.selectedInstance?.data}
                              registry={registry}
                              iconClassName="w-4 h-4"
                              className="flex h-full w-full items-center justify-center"
                            />
                          </div>
                          {slot.required && (
                            <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white bg-red-500"></div>
                          )}
                        </div>

                        {/* Search Input or Selected State - Full Width */}
                        <div className="flex-1 relative min-w-0">
                          {slot.selectedInstance ? (
                            <div className="space-y-4">
                              {/* Selected Entity Card - Input Style */}
                              <div className="relative">
                                <Input
                                  readOnly
                                  value={slot.selectedInstance.label}
                                  className={`w-full pl-11 pr-11 h-11 text-sm rounded-xl transition-all cursor-default border-blue-200 bg-blue-50/30 text-blue-900 font-medium`}
                                />
                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                                  {slot.missingAttributes?.length ? (
                                    <div className="h-4 w-4 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center">
                                      <div className="h-1.5 w-1.5 bg-current rounded-full" />
                                    </div>
                                  ) : (
                                    <div className="h-4 w-4 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                      <Check className="h-2.5 w-2.5" />
                                    </div>
                                  )}
                                </div>
                              </div>

                              <EntityImagePicker
                                source={slot.selectedInstance.data}
                                onSelect={(option) => handleSelectEntityImage(slot.slotKey, option)}
                              />

                              {/* Missing Attributes Inputs - Stacked */}
                              {slot.missingAttributes && slot.missingAttributes.length > 0 && (
                                <div className="space-y-4">
                                  {/* Validation Hint (Subtle) */}
                                  <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 px-3 py-2 rounded-lg inline-flex w-full">
                                    <span className="h-1.5 w-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                                    <span>Bitte ergänzen Sie die markierten Felder manuell</span>
                                  </div>
                                  
                                  <div className="flex flex-col gap-4">
                                    {slot.missingAttributes.map((attr) => (
                                      <div key={attr.attributePath} className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-700 flex items-center gap-1 ml-1">
                                          {getAttributeLabel(registry, attr.attributePath, slot.entityType)}
                                          <span className="text-orange-500" title="Pflichtfeld">*</span>
                                        </label>
                                        <SelectionStaticInputRenderer
                                          mediaType={attr.mediaType}
                                          value={manualOverrides[slot.slotKey]?.[attr.attributePath]}
                                          onChange={(val) => onManualOverride && onManualOverride(slot.slotKey, attr.attributePath, val)}
                                          className=""
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="relative flex-1">
                              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                ref={(el) => inputRefs.current[slot.slotKey] = el}
                                placeholder={`${slot.displayLabel} auswählen...`}
                                value={searchTerms[slot.slotKey] || ''}
                                onChange={(e) => handleSearchTermChange(slot.slotKey, e.target.value)}
                                onFocus={() => handleInputFocus(slot.slotKey)}
                                onBlur={handleInputBlur}
                                className="w-full pl-11 pr-11 h-11 text-sm border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl transition-all placeholder:truncate"
                              />
                              {isSearching[slot.slotKey] && (
                                <Loader2 className="absolute right-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="flex-shrink-0">
                          {slot.selectedInstance ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeselectInstance(slot.slotKey)}
                              className="h-11 w-11 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          ) : (
                            <div className="h-11 w-11 flex items-center justify-center">
                              {isLoadingEntity[slot.slotKey] ? (
                                <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                              ) : (
                                <div className="h-4 w-4 border-2 border-gray-300 rounded-full" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          {slotArray.length > 0 && selectorArray.length > 0 && (
            <div className="border-t border-gray-100"></div>
          )}

          {selectorArray.length > 0 && (
            <div className="p-4 sm:p-6">
              <div className="space-y-4 sm:space-y-5">
                {selectorArray.map(sel => (
                  <div key={sel.selectorId} className="flex items-start gap-3 sm:gap-4 group">
                    <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-600 group-hover:bg-purple-50 group-hover:text-purple-600 transition-all flex-shrink-0">
                      {getMediaTypeIcon(sel.mediaType, "w-4 h-4")}
                    </div>
                    <div className="flex-1">
                      <SelectionStaticInputRenderer
                        value={sel.selectedValue}
                        onChange={(val) => handleSelectorValueChange(sel.selectorId, val)}
                        mediaType={sel.mediaType}
                        className=""
                        selectionId={sel.selectorId}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Spacer for fixed footer on mobile */}
          <div className="h-28 lg:hidden" aria-hidden="true" />
          </div>
        </div>

        {/* Footer (Pinned at bottom on Desktop) */}
        <div className="fixed bottom-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 bg-white border-t border-gray-100 lg:static lg:z-auto lg:flex-none">
          {allRequiredSelected ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-10 w-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-gray-900 block">Vollständig</span>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">Alle benötigten Inhalte ausgewählt</p>
                </div>
              </div>
              {onProceedToPreview && (
                <Button 
                  onClick={onProceedToPreview}
                  data-onboarding="create-post-selection-continue"
                  disabled={isOnboardingSelectionButtonLocked}
                  className="h-11 px-6 bg-blue-600 text-white hover:bg-blue-700 rounded-xl text-sm font-medium shadow-sm transition-all flex-shrink-0"
                >
                  <span>Weiter</span>
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <div className="h-2.5 w-2.5 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
              <div className="min-w-0">
                <span className="text-sm font-semibold text-gray-900 block">
                  {requiredSlots.length - selectedRequiredSlots.length} Inhalte fehlen
                </span>
                <p className="text-xs text-gray-500 mt-0.5 truncate">Bitte alle Pflichtfelder ausfüllen</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Dropdown Portal */}
      {renderDropdown()}
    </>
  );
};
